import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import {
  AuthenticationModel,
  CommonAuthProcessProps,
  EnrollModel,
  SetupModel
} from '@models'
import { AuthenticationProvider, AuthenticationStatus, AuthStep } from '@enums'
import AuthenticationApi from '@utils/authentication-api'
import ApiService from '@utils/payer-auth-api'

interface Props extends CommonAuthProcessProps {
  authProvider: AuthenticationProvider
  checkAuthResultDelay?: number
  sendNotificationDelay?: number
}

type SelectNextStepProps = {
  authData: AuthenticationModel
  collectionUrl?: string | null
}

const useAuthProcess = ({
  authProvider,
  initialAuthentication,
  showBrand,
  checkAuthResultDelay = 0,
  sendNotificationDelay = 0
}: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [authentication, setAuthentication] = useState<AuthenticationModel>()
  const [authStep, setAuthStep] = useState<AuthStep>(AuthStep.PRE_SETUP)
  const [setupData, setSetupData] = useState<SetupModel>()
  const [stepUpData, setStepUpData] = useState<EnrollModel>()

  const navigate = useNavigate()
  const { id } = useParams()
  const checkedId = id ?? ''

  const delay = useCallback(
    (ms: number) => new Promise((resolve) => setTimeout(resolve, ms)),
    []
  )

  const handleError = useCallback(
    (error?: unknown) => {
      if (error) Sentry.captureException(error)
      navigate('/error', { state: { showBrand } })
    },
    [navigate, showBrand]
  )

  const withLoading = useCallback(
    async <T,>(asyncFunction: () => Promise<T>): Promise<T | undefined> => {
      setIsLoading(true)
      try {
        return await asyncFunction()
      } catch (error) {
        handleError(error)
        return undefined
      } finally {
        setIsLoading(false)
      }
    },
    [handleError]
  )

  const emitEventResult = useCallback((authData: AuthenticationModel) => {
    AuthenticationApi.emitAuthenticationResult(authData)
  }, [])

  const checkIfNotificationFormIsDisplayed = useCallback(
    async (authData: AuthenticationModel) => {
      const externalReturnUrl = authData.externalReturnUrl || ''
      const validatedExternalReturnUrl = externalReturnUrl.trim()
      const isRedirectNotification = validatedExternalReturnUrl !== ''

      await delay(sendNotificationDelay)

      if (isRedirectNotification) {
        setAuthStep(AuthStep.NOTIFICATION)
      } else {
        setAuthStep(AuthStep.RESULT)
        emitEventResult(authData)
      }
    },
    [emitEventResult, sendNotificationDelay, delay]
  )

  const checkAuthenticationResult = useCallback(
    async (result = {}) =>
      withLoading(async () => {
        await delay(checkAuthResultDelay)

        const response =
          authProvider === AuthenticationProvider.PAYWAY
            ? await ApiService.post<AuthenticationModel>(
                `confirmation/payway/${checkedId}`,
                result
              )
            : await AuthenticationApi.get(checkedId)

        if (!response) {
          handleError()
          return
        }

        setAuthentication(response)
        await checkIfNotificationFormIsDisplayed(response)
      }),
    [
      withLoading,
      delay,
      checkAuthResultDelay,
      authProvider,
      checkedId,
      handleError,
      checkIfNotificationFormIsDisplayed
    ]
  )

  const selectNextStep = useCallback(
    async ({ authData, collectionUrl }: SelectNextStepProps) => {
      if (authData.status !== AuthenticationStatus.PENDING) {
        await checkIfNotificationFormIsDisplayed(authData)
        return
      }

      async function handleStandardFlow() {
        if (authStep === AuthStep.PRE_SETUP) {
          setAuthStep(AuthStep.SETUP)
        } else if (authStep === AuthStep.SETUP) {
          setAuthStep(AuthStep.STEP_UP)
        }
      }

      async function handleInswitchFlow() {
        if (authStep !== AuthStep.PRE_SETUP) return

        if (collectionUrl) {
          setAuthStep(AuthStep.STEP_UP) // Friction flow
        } else {
          await checkAuthenticationResult() // Frictionless flow
        }
      }

      async function handlePowertranzFlow() {
        if (authStep === AuthStep.PRE_SETUP) {
          setAuthStep(AuthStep.STEP_UP)
        } else if (authStep === AuthStep.STEP_UP) {
          await checkAuthenticationResult()
        }
      }

      const providerFlows = {
        [AuthenticationProvider.INSWITCH]: handleInswitchFlow,
        [AuthenticationProvider.CYBERSOURCE]: handleStandardFlow,
        [AuthenticationProvider.PAYWAY]: handleStandardFlow,
        [AuthenticationProvider.SANDBOX]: handleStandardFlow,
        [AuthenticationProvider.POWERTRANZ]: handlePowertranzFlow
      }

      const flowHandler = providerFlows[authProvider]

      if (flowHandler) {
        await flowHandler()
      }
    },
    [
      authProvider,
      authStep,
      checkIfNotificationFormIsDisplayed,
      checkAuthenticationResult
    ]
  )

  const getSetupPayerParams = useCallback(
    async () =>
      withLoading(async () => {
        const response = await ApiService.post<SetupModel>(
          `setup/${checkedId}`,
          {}
        )

        if (!response) {
          handleError()
          return
        }

        const { authentication: authData, collectionUrl } = response

        setSetupData(response)
        setAuthentication(authData)
        selectNextStep({ authData, collectionUrl })
      }),
    [withLoading, checkedId, handleError, selectNextStep]
  )

  const getStepUpPayerParams = useCallback(
    async () =>
      withLoading(async () => {
        const referenceId =
          authProvider === AuthenticationProvider.PAYWAY
            ? ''
            : setupData?.referenceId

        const response = await ApiService.post<EnrollModel>(
          `enroll/${checkedId}`,
          {
            referenceId,
            authenticationId: id
          }
        )

        if (!response) {
          handleError()
          return
        }

        const { authentication: authData } = response
        setStepUpData(response)
        setAuthentication(authData)

        selectNextStep({ authData })
      }),
    [
      withLoading,
      checkedId,
      handleError,
      id,
      setupData?.referenceId,
      selectNextStep
    ]
  )

  useEffect(() => {
    const initializeAuth = async () => {
      if (
        initialAuthentication &&
        initialAuthentication.status !== AuthenticationStatus.PENDING
      ) {
        setAuthentication(initialAuthentication)
        setIsLoading(false)
        await checkIfNotificationFormIsDisplayed(initialAuthentication)
      } else {
        await getSetupPayerParams()
      }
    }

    let isMounted = true
    if (isMounted) {
      initializeAuth()
    }

    return () => {
      isMounted = false
    }
  }, [initialAuthentication, checkIfNotificationFormIsDisplayed])

  return {
    isLoading,
    authStep,
    authentication,
    setupData,
    stepUpData,
    checkAuthenticationResult,
    getStepUpPayerParams
  }
}

export default useAuthProcess
